import {
  IBudget,
  TBudgetSigningMode,
  TBudgetStatus,
} from 'modules/checkout/domain/entities';
import { TChannelType } from 'shared/domain/entities';
import {
  Customer,
  IVectisCustomer,
  PAYMENT_METHOD_TO_DOMAIN,
  TVectisPaymentMethodType,
} from 'shared/infra/adapters';

export type TVectisStatus =
  | 'ABERTO'
  | 'AGUARDANDO_APROVACAO'
  | 'AGUARDANDO_ASSINATURA'
  | 'AGUARDANDO_PAGAMENTO'
  | 'AGUARDANDO_PROCESSAMENTO'
  | 'AGUARDANDO_REABERTURA'
  | 'APROVACAO_PAGAMENTO'
  | 'CONTRATO_GERADO'
  | 'CONTRATO_FECHADO'
  | 'EM_PROCESSO_PAGAMENTO'
  | 'FECHADO'
  | 'ORCAMENTO_FECHADO'
  | 'INICIADO'
  | 'PERDIDO';

interface IVectisPaymentMethod {
  key: TVectisPaymentMethodType;
  titulo: string;
  descricao: string;
  maximoParcelas: number;
  valorParcela: number;
  percentualDesconto: number;
  precoDe: number;
  precoPor: number;
  percentualDescontoFormaPagamento: number;
  valorDescontoFormaPagamento: number;
  valorRecebimentoFormaPagamento?: number;
  diferencaParaValorPadraoOrcamento?: number;
  parcelas: {
    quantidade: number;
    valor: number;
  }[];
}

export interface IVectisBudget {
  idOrcamento: number;
  hashOrcamento: string;
  idUnidade: number;
  cliente: IVectisCustomer;
  statusOrcamento: TVectisStatus;
  itens: {
    id: number;
    titulo: string;
    isCombo: boolean;
    quantidadeSessoes: number;
    urlImagem: string;
    urlImagemAltaResolucaoEcommerce: string;
    urlImagemBaixaResolucaoEcommerce: string;
    urlImagemMasculinaAltaResolucao: string;
    urlImagemMasculinaBaixaResolucao: string;
    urlImagemFemininaAltaResolucao: string;
    urlImagemFemininaBaixaResolucao: string;
    classificacao: string[];
    avisos: string[];
    msgBloqueioVenda?: string;
    preco: {
      precoDe: number;
      precoPor: number;
      maximoParcelasRecorrente: number;
      valorParcelaRecorrente: number;
    };
    brindes?: any;
    tipo?: any;
    precos: {
      [key: string]: IVectisPaymentMethod;
    }[];
  }[];
  formasPagamento: IVectisPaymentMethod[];
  configuracoes: {
    parcelaReduzida: {
      habilitado: boolean;
      valorMinimoOrcamento: number;
      valorParcela: number;
      numeroMaximoParcelasFixas: number;
    };
    adiamentoProximaParcela: {
      condicao: string;
      habilitado: boolean;
      qtdMesesAdiamentoProximaParcela: number;
    };
  };
  cupom?: {
    id: number;
    codigo: string;
    percentualDesconto?: number;
    valorDesconto?: number;
  };
  dataHoraExpiracao?: string;
  formaDePagamentoEscolhida: TVectisPaymentMethodType;
  parcelasEscolhidas: number;
  mensagemNumeroTentativasLinkPagamento: string;
  numeroTentativasLinkPagamento: number;
  parametroNumeroTentativasLinkPagamento: number;
  dadosPagamento?: {
    promocaoParcelaReduzidaValorFixo: boolean;
    formaPagamento: TVectisPaymentMethodType;
    numeroParcelas: number;
    condicaoPagtoBoletoFlex: any;
    cobrancaCheckoutIsenta: boolean;
    condicaoPagamento?: unknown[];
  };
  canaisEnvioCodigoConfirmacao: TChannelType[];
  valorMaximoParcelaPermitidoContratoRecorrente: number;
  tipoAssinaturaContrato: TBudgetSigningMode;
}

export const STATUS_DICTIONARY: Record<TVectisStatus, TBudgetStatus> = {
  ABERTO: 'OPENED',
  AGUARDANDO_APROVACAO: 'WAITING_APPROVAL',
  AGUARDANDO_ASSINATURA: 'WAITING_SIGNATURE',
  AGUARDANDO_PAGAMENTO: 'WAITING_PAYMENT',
  AGUARDANDO_PROCESSAMENTO: 'WAITING_PROCESSING',
  AGUARDANDO_REABERTURA: 'WAITING_OPENING',
  APROVACAO_PAGAMENTO: 'PAYMENT_APPROVAL',
  CONTRATO_GERADO: 'CONTRACTED',
  CONTRATO_FECHADO: 'CLOSED',
  EM_PROCESSO_PAGAMENTO: 'PAYMENT_PROCESSING',
  FECHADO: 'CLOSED',
  ORCAMENTO_FECHADO: 'CLOSED',
  INICIADO: 'INITIATED',
  PERDIDO: 'LOST',
};

export class Budget implements IBudget {
  public id: IBudget['id'];
  public hashId: IBudget['hashId'];
  public unitId: IBudget['unitId'];
  public selectedInstallments: IBudget['selectedInstallments'];
  public selectedPaymentType: IBudget['selectedPaymentType'];
  public status: IBudget['status'];
  public signingMode: IBudget['signingMode'];
  public simultaneousPaymentMethods: IBudget['simultaneousPaymentMethods'];
  public items: IBudget['items'];
  public paymentMethods: IBudget['paymentMethods'];
  public disabledPaymentMethods: IBudget['disabledPaymentMethods'] = [];
  public customer: IBudget['customer'];
  public coupon: IBudget['coupon'];
  public enabledConfirmationChannels: IBudget['enabledConfirmationChannels'];
  public expiryDate: IBudget['expiryDate'];
  public paymentConditionMaxValue: IBudget['paymentConditionMaxValue'];
  public mensagemNumeroTentativasLinkPagamento: IBudget['mensagemNumeroTentativasLinkPagamento'];
  public numeroTentativasLinkPagamento: IBudget['numeroTentativasLinkPagamento'];
  public parametroNumeroTentativasLinkPagamento: IBudget['parametroNumeroTentativasLinkPagamento'];

  constructor(vectisBudget: IVectisBudget) {
    this.id = vectisBudget.idOrcamento;
    this.hashId = vectisBudget.hashOrcamento;
    this.unitId = vectisBudget.idUnidade;

    this.selectedInstallments = vectisBudget.parcelasEscolhidas;
    this.selectedPaymentType =
      PAYMENT_METHOD_TO_DOMAIN[vectisBudget.formaDePagamentoEscolhida];
    this.status = STATUS_DICTIONARY[vectisBudget.statusOrcamento];
    this.signingMode = vectisBudget.tipoAssinaturaContrato;

    this.paymentMethods = this.buildPaymentMethods(
      vectisBudget.formasPagamento,
    );

    this.customer = new Customer(vectisBudget.cliente);
    this.items = this.buildItems(vectisBudget.itens);

    this.paymentConditionMaxValue =
      vectisBudget.valorMaximoParcelaPermitidoContratoRecorrente;
    this.mensagemNumeroTentativasLinkPagamento =
      vectisBudget.mensagemNumeroTentativasLinkPagamento;
    this.numeroTentativasLinkPagamento =
      vectisBudget.numeroTentativasLinkPagamento;
    this.parametroNumeroTentativasLinkPagamento =
      vectisBudget.parametroNumeroTentativasLinkPagamento;
    this.expiryDate = vectisBudget.dataHoraExpiracao;
    this.enabledConfirmationChannels =
      vectisBudget.canaisEnvioCodigoConfirmacao;

    this.simultaneousPaymentMethods =
      vectisBudget.dadosPagamento?.condicaoPagamento?.length || 1;

    const hasItemsWithNotices =
      vectisBudget.itens.filter(item => item.avisos?.length).length > 0;

    if (hasItemsWithNotices) {
      this.disabledPaymentMethods.push('RECURRING_CARD');
    } else {
      const recurringPaymentMethod = vectisBudget.formasPagamento?.find(
        paymentMethod => paymentMethod.key === 'CARTAO_RECORRENTE',
      );

      if (recurringPaymentMethod && !!this.paymentConditionMaxValue) {
        const isInstallmentValueGreaterThanMaxValue =
          recurringPaymentMethod.valorParcela > this.paymentConditionMaxValue;

        if (isInstallmentValueGreaterThanMaxValue) {
          this.disabledPaymentMethods.push('RECURRING_CARD');
        }
      }
    }

    if (vectisBudget.cupom) {
      this.coupon = {
        id: vectisBudget.cupom.id,
        code: vectisBudget.cupom.codigo,
        discount: {
          value: vectisBudget.cupom.valorDesconto,
          percentace: vectisBudget.cupom.percentualDesconto,
        },
      };
    }
  }

  private buildItems(vectisItems: IVectisBudget['itens']): IBudget['items'] {
    return vectisItems.map(item => ({
      id: item.id,
      title: item.titulo,
      type: item.isCombo ? 'COMBO' : 'PACKAGE',
      sessions: item.quantidadeSessoes,
      image:
        this.customer.gender === 'MALE'
          ? item.urlImagemMasculinaBaixaResolucao
          : item.urlImagemFemininaBaixaResolucao,
      classification: item.classificacao,
      values: {
        from: item.preco.precoDe,
        to: item.preco.precoPor,
        recurrent: {
          maxInstallments: item.preco.maximoParcelasRecorrente,
          installmentValue: item.preco.valorParcelaRecorrente,
        },
      },
      notices: item.avisos,
      blockingMessage: item.msgBloqueioVenda,
    }));
  }

  private buildPaymentMethods(
    vectisMethods: IVectisPaymentMethod[],
  ): IBudget['paymentMethods'] {
    return vectisMethods
      .filter(method => !!method.key)
      .map(method => ({
        key: PAYMENT_METHOD_TO_DOMAIN[method.key],
        title: method.titulo,
        description: method.descricao,
        discount: {
          percentage: {
            default: method.percentualDesconto,
            byPaymentMethod: method.valorDescontoFormaPagamento,
          },
          value: method.valorDescontoFormaPagamento,
        },
        values: {
          from: method.precoDe,
          to: method.precoPor,
          discountInRelationToBudgetValue:
            method.diferencaParaValorPadraoOrcamento || 0,
        },
        installments: method.parcelas.map(installment => ({
          number: installment.quantidade,
          value: installment.valor,
        })),
      }));
  }
}
